import React, { useState, useEffect } from 'react';

const baseStyles = {
  shell: {
    maxWidth: 600,
    margin: '0 auto',
    padding: '24px 16px',
    fontFamily: '"Segoe UI", Arial, sans-serif',
    color: '#0f172a',
  },
  card: {
    background: '#ffffff',
    borderRadius: 20,
    boxShadow: '0 8px 30px rgba(15, 23, 42, 0.1)',
    overflow: 'hidden',
  },
  header: {
    padding: '28px 28px 0',
    textAlign: 'center',
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 800,
    color: '#0f172a',
  },
  subtitle: {
    marginTop: 8,
    color: '#64748b',
    fontSize: 15,
    lineHeight: 1.5,
  },
  form: {
    padding: 24,
  },
  field: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    marginBottom: 8,
    fontSize: 14,
    fontWeight: 600,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #d1d5db',
    borderRadius: 12,
    fontSize: 15,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: '#fff',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #d1d5db',
    borderRadius: 12,
    fontSize: 15,
    outline: 'none',
    resize: 'vertical',
    minHeight: 120,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: '#fff',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  submitBtn: {
    width: '100%',
    padding: '16px 24px',
    border: 'none',
    borderRadius: 12,
    background: '#2563eb',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  infoCards: {
    display: 'grid',
    gap: 16,
    marginTop: 24,
    padding: '0 24px 24px',
  },
  infoCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    padding: 16,
    background: '#f8fafc',
    borderRadius: 12,
  },
  infoIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: '#eff6ff',
    color: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    flexShrink: 0,
  },
  infoTitle: {
    margin: '0 0 4px',
    fontSize: 15,
    fontWeight: 600,
    color: '#0f172a',
  },
  infoText: {
    margin: 0,
    fontSize: 14,
    color: '#64748b',
    lineHeight: 1.5,
  },
  success: {
    padding: 20,
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 12,
    textAlign: 'center',
    color: '#166534',
  },
  successIcon: {
    fontSize: 48,
    marginBottom: 12,
    color: '#22c55e',
  },
  error: {
    padding: 14,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 12,
    color: '#b91c1c',
    fontSize: 14,
    marginBottom: 16,
  },
  fieldError: {
    color: '#dc2626',
    fontSize: 13,
    marginTop: 6,
  },
};

function injectStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('contact-form-styles')) return;

  const style = document.createElement('style');
  style.id = 'contact-form-styles';
  style.textContent = `
    @media (max-width: 640px) {
      .contact-shell { padding: 14px !important; }
      .contact-card { border-radius: 16px !important; }
    }
    .contact-input:focus, .contact-textarea:focus {
      border-color: #2563eb !important;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1) !important;
    }
    .contact-submit:hover { background: #1d4ed8 !important; }
    .contact-submit:disabled { background: #93c5fd !important; cursor: not-allowed !important; }
  `;
  document.head.appendChild(style);
}

export function ContactUsForm({
  title = 'Contact Us',
  subtitle = 'Have questions? Fill out the form and we will get back to you shortly.',
  showInfo = true,
  contactInfo = null,
  onSuccess,
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile_number: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    injectStyles();
  }, []);

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: '' }));
    }
  }

  function validateForm() {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = 'Name is required.';
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!form.mobile_number.trim()) {
      newErrors.mobile_number = 'Phone number is required.';
    } else if (!/^[\d\s\-+()]{7,20}$/.test(form.mobile_number)) {
      newErrors.mobile_number = 'Please enter a valid phone number.';
    }

    if (!form.message.trim()) {
      newErrors.message = 'Message is required.';
    } else if (form.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (!validateForm()) return;

    const submittedForm = { ...form };
    setSuccess(true);
    setForm({ name: '', email: '', mobile_number: '', message: '' });
    onSuccess?.(submittedForm);
  }

  const defaultContactInfo = {
    phone: contactInfo?.phone || '+1 234 567 890',
    email: contactInfo?.email || 'contact@hospital.com',
    address: contactInfo?.address || '123 Healthcare Street, Medical City',
  };

  return (
    <div style={baseStyles.shell} className="contact-shell">
      <div style={baseStyles.card} className="contact-card">
        <div style={baseStyles.header}>
          <h2 style={baseStyles.title}>{title}</h2>
          <p style={baseStyles.subtitle}>{subtitle}</p>
        </div>

        <form style={baseStyles.form} onSubmit={handleSubmit}>
          {success ? (
            <div style={baseStyles.success} role="status" aria-live="polite">
              <div style={baseStyles.successIcon}>
                <i className="fas fa-check-circle" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>Message Sent!</h3>
              <p style={{ margin: 0, fontSize: 14 }}>
                Thank you for contacting us. We will get back to you soon.
              </p>
              <button
                type="button"
                style={{ ...baseStyles.submitBtn, marginTop: 16 }}
                onClick={() => setSuccess(false)}
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <>
              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Your Name</label>
                <input
                  className="contact-input"
                  style={{
                    ...baseStyles.input,
                    borderColor: errors.name ? '#dc2626' : '#d1d5db',
                  }}
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Enter your full name"
                />
                {errors.name && <div style={baseStyles.fieldError}>{errors.name}</div>}
              </div>

              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Email Address</label>
                <input
                  className="contact-input"
                  style={{
                    ...baseStyles.input,
                    borderColor: errors.email ? '#dc2626' : '#d1d5db',
                  }}
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="Enter your email"
                />
                {errors.email && <div style={baseStyles.fieldError}>{errors.email}</div>}
              </div>

              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Phone Number</label>
                <input
                  className="contact-input"
                  style={{
                    ...baseStyles.input,
                    borderColor: errors.mobile_number ? '#dc2626' : '#d1d5db',
                  }}
                  type="tel"
                  value={form.mobile_number}
                  onChange={(e) => updateField('mobile_number', e.target.value)}
                  placeholder="Enter your phone number"
                />
                {errors.mobile_number && <div style={baseStyles.fieldError}>{errors.mobile_number}</div>}
              </div>

              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Your Message</label>
                <textarea
                  className="contact-textarea"
                  style={{
                    ...baseStyles.textarea,
                    borderColor: errors.message ? '#dc2626' : '#d1d5db',
                  }}
                  value={form.message}
                  onChange={(e) => updateField('message', e.target.value)}
                  placeholder="How can we help you?"
                />
                {errors.message && <div style={baseStyles.fieldError}>{errors.message}</div>}
              </div>

              <button
                className="contact-submit"
                type="submit"
                style={baseStyles.submitBtn}
              >
                <i className="fas fa-paper-plane" />
                Send Message
              </button>
            </>
          )}
        </form>

      
      </div>
    </div>
  );
}

export default ContactUsForm;
